import { ReactElement, useEffect, useMemo, useState } from 'react';
import { DealersList, IPrimaryDealerProps, TPrimaryDetails } from '.';
import { ActionButton, Dropdown, TextInput } from '@components/common';
import { useAddRemoveAction } from '@hooks/useAddRemoveAction';
import { useDealerListRxDb } from '@services/hooks/enduser-details/rxdbHooks/useDealerListRxDb';
import { useDebounce } from '@hooks/useDebounce';
import { useEndUserProfileStore } from '@store/endUserProfileStore';

export const PrimaryDetailList = (props: IPrimaryDealerProps): ReactElement => {
	const { formik, primaryListIndex, fieldName, isEditing, dealer, isDealerDetail } = props;
	const [dealerSearch, setDealerSearch] = useState<string>('');
	const { result: dealerList } = useDealerListRxDb({
		searchParams: [
			{ key: 'dealername_c', value: dealerSearch },
			{ key: 'dealercode_c', value: dealerSearch },
		],
		sortBy: 'dealername_c',
	});
	const {
		moreDetailsRevalidate,
		setSubKeyBaseValue: setEndUserKeyBaseValue,
		setSubKeyValue,
		moreDetailFormRevalidateField,
	} = useEndUserProfileStore();

	const addNewDeatils = {
		id: formik.values[fieldName].length,
		dealer_code: null,
		dealer_name: null,
	};

	const { handleAddDetails, handleRemoveDetails } = useAddRemoveAction({
		formik,
		addNewDeatils,
		fieldName,
	});

	const dealersList = useMemo((): DealersList[] => {
		if (dealerList?.length) {
			return dealerList.map((d, index) => ({
				id: index,
				value: `${d._data?.dealercode_c} -  ${d._data?.dealername_c}`,
				name: d._data?.dealername_c,
			}));
		}
		return [];
	}, [dealerList]);

	const onDropDownSearch = useDebounce((e: React.KeyboardEvent<HTMLInputElement>): void => {
		const element = e.target as HTMLInputElement;
		setDealerSearch(element.value);
	}, 300);

	useEffect(() => {
		const updatedDealerName = dealersList?.find(
			(t: DealersList) => t.value === formik.values[fieldName][primaryListIndex].dealer_code
		)?.name;
		if (updatedDealerName) {
			formik.setFieldValue(`${fieldName}[${primaryListIndex}].dealer_name`, updatedDealerName);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dealersList, formik.values[fieldName][primaryListIndex].dealer_code]);

	const filteredDealers = useMemo(() => {
		const selectedDealerCodes = formik.values[fieldName].map((detail: TPrimaryDetails) => detail.dealer_code);
		return dealersList?.filter((dealer: DealersList) => !selectedDealerCodes.includes(dealer.value));
	}, [dealersList, fieldName, formik.values]);

	const handleAddRemoveRevalidateField = (childIndex: number | undefined, remove = true): void => {
		if (!moreDetailsRevalidate) return;
		const value: boolean[] = [...moreDetailFormRevalidateField.primary_details];
		if (childIndex && remove) {
			value.splice(childIndex, 1);
			setSubKeyValue('moreDetailFormRevalidateField', 'primary_details', value);
			return;
		}
		value.push(true);
		setSubKeyValue('moreDetailFormRevalidateField', 'primary_details', value);
	};

	return (
		<li className="d-col">
			<div className="multi-input-box">
				<div className="inputs-wrap">
					<div>
						<Dropdown
							id={`dealer_code_${primaryListIndex}`}
							name={`${fieldName}[${primaryListIndex}].dealer_code`}
							label="Dealer Code"
							title={dealer?.dealer_code?.split('-')[0] ?? ''}
							onBlur={formik.handleBlur}
							options={filteredDealers}
							allOptions={dealersList}
							setFieldValue={formik.setFieldValue}
							handelKeyDownProp={onDropDownSearch}
							listFetching={true}
							error={
								formik.touched[fieldName] &&
								formik.touched[fieldName][primaryListIndex]?.dealer_code &&
								formik.errors[fieldName]
									? formik.errors[fieldName][primaryListIndex]?.dealer_code
									: null
							}
							onChange={() => {
								setEndUserKeyBaseValue('moreDetailFormRevalidateField', 'primary_details', primaryListIndex, true);
							}}
							disabled={isEditing}
							required
							revalidate={moreDetailsRevalidate && !moreDetailFormRevalidateField.primary_details[primaryListIndex]}
							revalidateError={'Please revalidate dealer code'}
						/>
					</div>

					<div>
						<div className="form-control">
							<TextInput
								type="text"
								id={`dealer_name_${primaryListIndex}`}
								name={`${fieldName}[${primaryListIndex}].dealer_name`}
								onBlur={formik.handleBlur}
								value={dealer?.dealer_name ?? ''}
								setFieldValue={formik.setFieldValue}
								label="Dealer Name"
								required
								disabled
							/>
						</div>
					</div>
				</div>
				<div className="add-multi-input">
					{formik.values[fieldName].length > 1 && primaryListIndex !== 0 && !isDealerDetail && (
						<ActionButton
							label="Remove"
							handlerFn={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, childIndex: number | undefined) => {
								handleRemoveDetails(e, childIndex);
								handleAddRemoveRevalidateField(childIndex);
							}}
							className="remove-btn"
							childIndex={primaryListIndex}
							action="remove"
							disabled={isEditing}
						/>
					)}
					{primaryListIndex === formik.values[fieldName].length - 1 &&
						formik.values[fieldName].length < 3 &&
						!isDealerDetail && (
							<ActionButton
								label="Add More"
								handlerFn={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, childIndex: number | undefined) => {
									handleAddDetails(e);
									handleAddRemoveRevalidateField(childIndex, false);
								}}
								disabled={isEditing}
								action="add"
							/>
						)}
				</div>
			</div>
			<p className="error-message">Please Enter the valid field</p>
		</li>
	);
};
